import React from "react";
import {Box, Chip, Colors, Container, Text} from "@bounce/shared/ui";
import {ReachSegment, SegmentCategory} from "../../../apis/requests/segments/types";
import {
  CATEGORY_FALLBACK_CONDITIONS_LABEL,
  CATEGORY_FALLBACK_PENDING_HINT,
  SEGMENT_CATEGORY_DESCRIPTION,
  SEGMENT_CATEGORY_INFO,
} from "./constants";
import {SegmentCategoryMarker} from "../SegmentCategoryMarker";

type Props = {
  segmentCategory: SegmentCategory;
  currentName: string;
  buckets: ReachSegment[];
  currentSegmentId: number | undefined;
  onOpenBucket: (segment: ReachSegment) => void;
  switchingBlocked: boolean;
};

type BucketRowProps = {
  name: string;
  description: string | null;
  ruleNames: string[];
  isCurrent: boolean;
  onClick?: () => void;
};

const UNNAMED_BUCKET = "Unnamed bucket";
const CURRENT_BUCKET_SUFFIX = "(this bucket)";
const OPEN_BUCKET_HINT = "Click a bucket to switch to it.";
const SWITCHING_BLOCKED_HINT = "Apply or cancel your changes to switch to another bucket.";

const BucketRow = ({name, description, ruleNames, isCurrent, onClick}: BucketRowProps) => (
  <Container
    row
    fullWidth
    onClick={onClick}
    sx={{
      gap: 1.5,
      alignItems: "center",
      justifyContent: "space-between",
      px: 1.5,
      py: 1,
      borderRadius: "8px",
      backgroundColor: isCurrent ? Colors.blue._200 : Colors.bw.white,
      border: `1px solid ${isCurrent ? Colors.blue._400 : Colors.tertiary._200}`,
      transition: "border-color 0.15s, background-color 0.15s",
      ...(onClick && {"&:hover": {borderColor: Colors.blue._400, backgroundColor: Colors.blue._100}}),
    }}
  >
    <Box sx={{minWidth: 0}}>
      <Text size={13} variant="inter600" sx={{color: Colors.grey._900}}>
        {name || UNNAMED_BUCKET}
        {isCurrent && (
          <Box component="span" sx={{color: Colors.blue._800, fontWeight: 400, ml: 1}}>
            {CURRENT_BUCKET_SUFFIX}
          </Box>
        )}
      </Text>
      {description && (
        <Text size={12} sx={{color: Colors.grey._500}}>
          {description}
        </Text>
      )}
    </Box>
    <Box sx={{display: "flex", flexWrap: "wrap", gap: 0.5, justifyContent: "flex-end"}}>
      {ruleNames.map((ruleName, index) => (
        <Chip key={index} size_="small" variant_="blue" label={ruleName} />
      ))}
    </Box>
  </Container>
);

const ruleNamesOf = (segment: ReachSegment) =>
  segment.is_category_fallback
    ? [CATEGORY_FALLBACK_CONDITIONS_LABEL]
    : segment.conditions.rules.map((rule, index) => rule.name || `Group ${index + 1}`);

const SegmentCategoryBucketsCard = ({
  segmentCategory,
  currentName,
  buckets,
  currentSegmentId,
  onOpenBucket,
  switchingBlocked,
}: Props) => {
  const {label, bucketsHint} = SEGMENT_CATEGORY_INFO[segmentCategory];
  const isCurrentListed = buckets.some((bucket) => bucket.id === currentSegmentId);
  const bucketCount = buckets.length + (isCurrentListed ? 0 : 1);
  const hasFallback = buckets.some((bucket) => bucket.is_category_fallback);
  const currentRow = <BucketRow name={currentName} description={null} ruleNames={[]} isCurrent />;
  const renderBucket = (bucket: ReachSegment) =>
    bucket.id === currentSegmentId ? (
      <React.Fragment key={bucket.id}>{currentRow}</React.Fragment>
    ) : (
      <BucketRow
        key={bucket.id}
        name={bucket.name}
        description={bucket.description}
        ruleNames={ruleNamesOf(bucket)}
        isCurrent={false}
        onClick={switchingBlocked ? undefined : () => onOpenBucket(bucket)}
      />
    );
  return (
    <Box
      sx={{
        width: "100%",
        backgroundColor: Colors.tertiary._100,
        borderRadius: "12px",
        padding: "20px",
        display: "flex",
        flexDirection: "column",
        gap: "12px",
      }}
    >
      <Box>
        <Container row sx={{gap: 1, alignItems: "center", justifyContent: "flex-start"}}>
          <SegmentCategoryMarker segmentCategory={segmentCategory} />
          <Text variant="interDisplay600" size={16} sx={{color: Colors.grey._800}}>
            Buckets in {label} ({bucketCount})
          </Text>
        </Container>
        <Text size={12} sx={{color: Colors.grey._600, mt: 0.5}}>
          {SEGMENT_CATEGORY_DESCRIPTION} Expected buckets: {bucketsHint}.{" "}
          {bucketCount > 1 && (switchingBlocked ? SWITCHING_BLOCKED_HINT : OPEN_BUCKET_HINT)}
          {!hasFallback && ` ${CATEGORY_FALLBACK_PENDING_HINT}`}
        </Text>
      </Box>
      <Box sx={{display: "flex", flexDirection: "column", gap: 1}}>
        {buckets.filter((bucket) => !bucket.is_category_fallback).map(renderBucket)}
        {!isCurrentListed && currentRow}
        {buckets.filter((bucket) => bucket.is_category_fallback).map(renderBucket)}
      </Box>
    </Box>
  );
};

export default SegmentCategoryBucketsCard;
